/**
 * fx — the combat VFX kit. One module, fire-and-forget, budgeted per tier.
 *
 * This is the seam between the rendering lane and the gameplay lane: the
 * signatures of `muzzle`, `trail`, `impact`, `expire`, `explosion`, `flash`,
 * `requestLight` and `slash` are fixed by SEAM.md and the projectile code calls
 * them without knowing what they draw. Everything else here is the same kit
 * used by AbilityVFX for the roster's abilities.
 *
 * ── What an effect is made of (pass 2) ─────────────────────────────────────
 *
 * Effects are cartoon OBJECTS, not light. Every beat is three to five layers
 * with DIFFERENT timing:
 *
 *   SHAPE    a drawn cel shape (`CelShapes`): the impact burst, the muzzle
 *            spike, the shock band, the swing crescent. Opaque-looking, in the
 *            identity colour with a white core and an ink rim. It pops in with
 *            overshoot on frame one and dies by noise EROSION.
 *   SHARDS   angular chunks (`ParticleSystem` SHARD) thrown with gravity and
 *            spin; they shrink out.
 *   PUFFS    cartoon clouds (`ParticleSystem` PUFF): hard-edged, cel-shaded,
 *            eroding. Dust, smoke, the death poof.
 *   SPARKS   the only additive thing: tiny hot streaks (`ParticleSystem` GLOW).
 *   GROUND   a thin fast shock line and a scorch (`GroundMarks`), deck chunks
 *            (`Debris`), a blob shadow under anything airborne (`BlobShadows`),
 *            and a continuous tapered ribbon behind anything that flies
 *            (`RibbonPool`).
 *
 * ── Colour ─────────────────────────────────────────────────────────────────
 *
 * `color` is a character's `colorHex` — its identity — normalised so the
 * brightest channel is 1 (a dark green must read as vividly as a pale grey);
 * the shaders build the core/body/ink ramp from it. Anything drawn ON THE
 * FLOOR takes `opts.floorColor` (`telegraphHex`): the deck spends red on
 * hazard and green on mend and a floor mark must never be mistaken for either.
 *
 * ── Budget ─────────────────────────────────────────────────────────────────
 *
 * Every pool is allocated once for the device's MAX tier and capped by the
 * CURRENT tier (`PostProcessing.TIER_CONFIG`). Counts scale by the tier's
 * `fxScale`; a full pool overwrites its oldest particle rather than dropping
 * the new effect. No call here allocates.
 */

import * as THREE from 'three/webgpu';
import { PLAYER_HALF_HEIGHT } from '@chaos/sim';
import { ParticleSystem, SHAPES, FADES } from './particles/ParticleSystem.js';
import { LightPool } from './LightPool.js';
import { GroundMarks } from './GroundMarks.js';
import { Debris } from './Debris.js';
import { RibbonPool } from './Ribbons.js';
import { BlobShadows } from './Shadows.js';
import { slashArc, starFlash, muzzleFlash, burstRing, smear, cracks as cracksShape, flatOnGround, releaseCelShape } from './CelShapes.js';
import { setCoreGain, popScale, erosionAt, identity, floorTint } from './CelLook.js';
import { triggerCameraShake, cameraFocus } from './ScreenEffects.js';
import { getTierConfig, getMaxTierConfig, onTierChange, tierConfigs } from '../PostProcessing.js';
import { registerDiagnostics } from '../performance/runtime.js';
import { easeOutCubic } from '../math.js';
import { DECAL_LIFT, DECAL_STACK } from '../combat/decals.js';

let _scene = null;
let glow = null;
let smoke = null;
let shards = null;
let lights = null;
let marks = null;
let debris = null;
let ribbons = null;
let shadows = null;
/** Count multiplier for the current tier. */
let _scale = 1;
/** Stride dust on or off for the current tier (`footsteps` in TIER_CONFIG). */
let _footsteps = true;
/**
 * Tracked cel shapes: { mesh, u, age, life, base, pop, popT, erodeFrom, kind }.
 * `base` is the resting scale; the shape pops in at base × (1 + pop) on frame
 * one and settles by popT of its life; erosion starts at erodeFrom.
 */
const _tracked = [];
let _stopTierListener = null;

/**
 * Lights are a supporting layer now, not the effect: a faint pool of colour
 * on the deck under a shot. 0.16 was invisible; 0.3 shows without whiting out
 * the toon deck.
 */
const LIGHT_GAIN = 0.3;

const WHITE = new THREE.Color(1, 1, 1);
const DECK_GREY = new THREE.Color(0x6b7480);
/** Deck dust and smoke: warm tan, and a pale grey-white. */
const DUST = new THREE.Color(0.86, 0.76, 0.58);
const SMOKE = new THREE.Color(0.9, 0.89, 0.87);
const _c = new THREE.Color();
const _d = new THREE.Color();
const R = (a, b) => a + Math.random() * (b - a);
const TAU = Math.PI * 2;
/** Camera pitch on the flat shapes drawn at chest height, so they keep area from 55° overhead. */
const TIP = 0.3;

/**
 * The vivid identity form of a colour (`CelLook.identity`), written into the
 * shared scratch and pulled `whiten` toward white.
 */
function ldr(color, whiten = 0) {
  identity(color, _c);
  if (whiten > 0) _c.lerp(WHITE, whiten);
  return _c;
}

/** A floor colour as authored (`CelLook.floorTint`), pulled `whiten` toward white. */
function floorLdr(color, whiten = 0) {
  floorTint(color, _c);
  if (whiten > 0) _c.lerp(WHITE, whiten);
  return _c;
}

/** The HDR form for the additive sparks: normalised, whitened, × gain. */
function hot(color, gain = 1, whiten = 0) {
  _c.set(color);
  const m = Math.max(_c.r, _c.g, _c.b, 1e-4);
  _c.multiplyScalar(1 / m);
  if (whiten > 0) _c.lerp(WHITE, whiten);
  _c.multiplyScalar(gain);
  return _c;
}

/** The sprite rotation that points a shape's +y down `dir` on this camera (yaw 0, pitch 55°). */
function screenSpin(dir) {
  return Math.atan2(-dir.x, -dir.z * 0.82);
}

/** Where the deck is under a point: given, or a body height below a chest point. */
function floorFor(pos, opts, chest) {
  if (opts && Number.isFinite(opts.floorY)) return opts.floorY;
  return chest ? pos.y - PLAYER_HALF_HEIGHT : pos.y;
}

function count(n) {
  if (_scale <= 0) return 0;
  return Math.max(1, Math.round(n * _scale));
}

// ─── Lifecycle ──────────────────────────────────────────────────

export function initFx(scene, renderer = null) {
  if (glow) disposeFx();
  _scene = scene;
  const max = getMaxTierConfig();
  glow = new ParticleSystem({ capacity: max.glowParticles, additive: true, shape: SHAPES.GLOW, name: 'glow' }).attach(scene);
  smoke = new ParticleSystem({ capacity: max.smokeParticles, additive: false, shape: SHAPES.PUFF, fade: FADES.ERODE, name: 'puffs' }).attach(scene);
  shards = new ParticleSystem({ capacity: max.shardParticles, additive: false, shape: SHAPES.SHARD, fade: FADES.CUT, name: 'shards' }).attach(scene);
  // The pool must be in the scene BEFORE any lit material compiles — see
  // LightPool: the light set is part of every material's program key.
  lights = new LightPool(max.lights, { gain: LIGHT_GAIN, fakes: Math.max(...Object.values(tierConfigs()).map((t) => t.fakeLights ?? 0)) }).attach(scene);
  marks = new GroundMarks({ rings: max.rings, decals: max.decals }).attach(scene);
  debris = new Debris(max.debris).attach(scene);
  ribbons = new RibbonPool(max.ribbons).attach(scene);
  shadows = new BlobShadows(max.shadows).attach(scene);
  applyTier(getTierConfig());
  _stopTierListener = onTierChange(applyTier);
  registerDiagnostics('fx', getFxStats);
  if (renderer && typeof renderer.compileAsync === 'function') void prewarm(renderer, scene);
}

function applyTier(cfg) {
  if (!glow) return;
  glow.setCap(cfg.glowParticles);
  smoke.setCap(cfg.smokeParticles);
  shards.setCap(cfg.shardParticles ?? 100);
  lights.setActive(cfg.lights, cfg.fakeLights ?? 0);
  marks.setCaps({ rings: cfg.rings, decals: cfg.decals });
  debris.setCap(cfg.debris);
  debris.setShadows(cfg.shadowSize > 0);
  ribbons.setCap(cfg.ribbons ?? 16);
  shadows.setCap(cfg.shadows ?? 32);
  setCoreGain(cfg.coreGain ?? 1);
  _scale = cfg.fxScale;
  _footsteps = cfg.footsteps !== false;
}

/**
 * Compile the kit's programs while the loading screen is up. A hidden mesh is
 * never built by the renderer, so without this the first muzzle flash of the
 * match would pay the compile. Same recipe as ShaderPrewarm: an isolated scene
 * that borrows the real one's lights, fog and environment.
 */
async function prewarm(renderer, scene) {
  const isolated = new THREE.Scene();
  isolated.fog = scene.fog ?? null;
  isolated.environment = scene.environment ?? null;
  // EVERY ring, scorch and fake light, not the first of each: each has its
  // own material, and the pools rotate through them — compiling only
  // `rings[0]` left rings 1..9 and decals 1..15 to compile on the slam or
  // the blast that first reached them, minutes into a session (the build
  // log named `ground-marks:scorch` under One More Thing's warm hitch).
  const meshes = [glow.mesh, smoke.mesh, shards.mesh, debris.mesh, ribbons.mesh, shadows.mesh,
    ...marks.rings.map((r) => r.mesh), ...marks.decals.map((d) => d.mesh), ...lights.fakes.map((f) => f.sprite)];
  const restore = meshes.map((m) => ({ m, visible: m.visible, count: m.count, parent: m.parent }));
  for (const m of meshes) {
    m.visible = true;
    if ('count' in m) m.count = 1;
    isolated.add(m);
  }
  try {
    await renderer.compileAsync(isolated, new THREE.PerspectiveCamera(), scene);
  } catch (err) {
    console.warn('[fx] shader warmup failed; first effects will compile on demand', err);
  } finally {
    for (const r of restore) {
      r.parent?.add(r.m);
      r.m.visible = r.visible;
      if ('count' in r.m) r.m.count = r.count;
    }
  }
}

/** Step the kit by the PRESENTATION delta (0 during hitstop). */
export function updateFx(dt) {
  if (!glow) return;
  glow.update(dt);
  smoke.update(dt);
  shards.update(dt);
  marks.update(dt);
  debris.update(dt);
  ribbons.update(dt);
  shadows.publish();
  for (let i = _tracked.length - 1; i >= 0; i--) {
    const fx = _tracked[i];
    fx.age += dt;
    const t = fx.age / fx.life;
    if (t >= 1) {
      releaseCelShape(fx.mesh);
      _tracked.splice(i, 1);
      continue;
    }
    const s = fx.base * popScale(t, fx.popT, fx.pop);
    if (fx.kind === 'band') {
      // The shock band grows from a third of the radius to the true radius,
      // fast, and STOPS there: the footprint is information.
      const grow = Math.min(1, t / fx.popT);
      fx.mesh.scale.setScalar(fx.base * (0.34 + 0.66 * easeOutCubic(grow)));
    } else if (fx.kind === 'slash') {
      fx.mesh.scale.setScalar(s);
      fx.u.progress.value = Math.min(t / 0.45, 1);
    } else if (fx.kind === 'fixed') {
      // Keeps the scale it was authored with (a dash smear's 0.6 x 2.6
      // stretch); only its erosion runs. setScalar here squashed every
      // smear into a unit square on its first frame.
    } else {
      fx.mesh.scale.setScalar(s);
    }
    if (fx.u.erode) fx.u.erode.value = erosionAt(t, fx.erodeFrom);
  }
  const f = cameraFocus();
  lights.setFocus(f.x, f.z);
  lights.update(dt);
}

export function clearFx() {
  if (!glow) return;
  glow.clear();
  smoke.clear();
  shards.clear();
  lights.clear();
  marks.clear();
  debris.clear();
  ribbons.clear();
  shadows.clear();
  for (const fx of _tracked) releaseCelShape(fx.mesh);
  _tracked.length = 0;
}

export function disposeFx() {
  if (!glow) return;
  clearFx();
  _stopTierListener?.();
  _stopTierListener = null;
  glow.dispose();
  smoke.dispose();
  shards.dispose();
  lights.dispose();
  marks.dispose();
  debris.dispose();
  ribbons.dispose();
  shadows.dispose();
  glow = smoke = shards = lights = marks = debris = ribbons = shadows = null;
  _scene = null;
}

export function getFxStats() {
  if (!glow) return null;
  return {
    scale: _scale,
    glow: { live: glow.live, cap: glow.cap, capacity: glow.capacity, ...glow.stats },
    smoke: { live: smoke.live, cap: smoke.cap, capacity: smoke.capacity, ...smoke.stats },
    shards: { live: shards.live, cap: shards.cap, capacity: shards.capacity, ...shards.stats },
    lights: { size: lights.size, active: lights.active, ...lights.stats },
    marks: { rings: marks.liveRings, decals: marks.liveDecals },
    debris: { live: debris.live, cap: debris.cap, ...debris.stats },
    ribbons: { live: ribbons.live, cap: ribbons.cap, ...ribbons.stats },
    shadows: { cap: shadows.cap, ...shadows.stats },
    tracked: _tracked.length,
  };
}

/** True once `initFx` has run — the seam no-ops before that. */
export function isFxReady() {
  return glow !== null;
}

/** The particle systems themselves, for tests that read what a recipe emitted. */
export function fxSystems() {
  return glow ? { glow, smoke, shards } : null;
}

// ─── Shape helpers ──────────────────────────────────────────────

/** Put a cel shape in the scene and on the tracked list. */
function track(mesh, u, { life, base = 1, pop = 0.3, popT = 0.22, erodeFrom = 0.35, kind = 'shape' }) {
  _scene?.add(mesh);
  mesh.scale.setScalar(base * (1 + pop));
  _tracked.push({ mesh, u, age: 0, life, base, pop, popT, erodeFrom, kind });
  return mesh;
}

/**
 * The impact burst: a jagged star at `pos`, `size` units across, living `life`
 * seconds. Pops in oversize on frame one, erodes out from `erodeFrom`.
 */
export function burstStar(pos, color, size = 1.2, life = 0.22, opts = {}) {
  if (!glow) return null;
  const { mesh, u } = starFlash(color, { additive: !!opts.additive });
  mesh.position.set(pos.x, pos.y, pos.z);
  mesh.renderOrder = 9;
  // An impact sits ON the body or IN the wall it hit; it must draw over both.
  mesh.material.depthTest = false;
  if (opts.gain) u.gain.value = opts.gain;
  return track(mesh, u, { life, base: size, pop: opts.pop ?? 0.35, popT: 0.2, erodeFrom: opts.erodeFrom ?? 0.3 });
}

/**
 * The shock band on the floor at exactly `radius` — the footprint of a blast
 * or a slam. Colour is a FLOOR colour. `thick` is the band's world thickness.
 */
export function shockBand(pos, radius, floorColor, life = 0.42, thick = null) {
  if (!glow) return null;
  const r = Math.max(0.4, radius);
  const t = thick ?? Math.min(Math.max(0.5, r * 0.34), 2.2);
  const { mesh, u } = burstRing(floorColor, { bandStart: 1 - t / r });
  // Lifted off the deck like every floor mark (the same lift GroundMarks.ring
  // gave the ring this replaced): callers pass the raw floor height, and a
  // depth-tested band at exactly that height z-fights the deck and flickers.
  flatOnGround(mesh, pos.x, pos.y + DECAL_LIFT + DECAL_STACK * 2, pos.z);
  mesh.renderOrder = 6;
  return track(mesh, u, { life, base: r, pop: 0, popT: 0.2, erodeFrom: 0.42, kind: 'band' });
}

// ─── The seam (SEAM.md) ─────────────────────────────────────────
//
// Positions are {x, y, z} in world units. `color` is 0xRRGGBB (a character
// colorHex). `dir` is a unit {x, z} on the ground plane. All calls are
// fire-and-forget and cheap: they no-op before init and never allocate.

/**
 * On every shot fired. Four layers: the spike flash down the barrel (frame
 * one, biggest, gone in 90 ms), sparks along the aim, one puff of smoke that
 * drifts forward, and a light on the deck.
 */
export function muzzle(pos, dir, color, scale = 1) {
  if (!glow) return;
  const fl = floorFor(pos, null, true);
  const dx = dir.x;
  const dz = dir.z;

  // A sprite turned to the aim on screen: it keeps its full area from the
  // 55° camera, where a flat spike foreshortened to a sliver.
  const { mesh, u } = muzzleFlash(color);
  mesh.position.set(pos.x + dx * 0.75 * scale, pos.y + 0.1, pos.z + dz * 0.75 * scale);
  u.spin.value = screenSpin(dir);
  mesh.material.depthTest = false;
  mesh.renderOrder = 9;
  track(mesh, u, { life: 0.09, base: 2.0 * scale, pop: 0.3, popT: 0.3, erodeFrom: 0.4 });

  let c = hot(color, 5, 0.3);
  const n = count(4 * scale);
  for (let i = 0; i < n; i++) {
    const spd = R(5, 10);
    glow.emit(
      pos.x + dx * 0.4, pos.y, pos.z + dz * 0.4,
      dx * spd + R(-1.6, 1.6), R(-0.3, 1.2), dz * spd + R(-1.6, 1.6),
      R(0.08, 0.16), 0.13 * scale, 0.03, c.r, c.g, c.b, 1, 4, 3, fl, 0.35, 0.18
    );
  }
  c = SMOKE;
  smoke.emit(pos.x + dx * 0.5, pos.y + 0.1, pos.z + dz * 0.5, dx * 2.2 + R(-0.3, 0.3), 0.7, dz * 2.2 + R(-0.3, 0.3), 0.32, 0.32 * scale, 0.75 * scale, c.r, c.g, c.b, 1, 3, -0.4, fl, 0, 0);
  c = hot(color, 1, 0.3);
  lights.flash(pos.x + dx * 0.3, pos.y + 0.1, pos.z + dz * 0.3, c.r, c.g, c.b, 6.5 * scale, 5.5, 0.09);
}

/**
 * A wake sample for something the RIBBONS do not carry (a slow shell without
 * a ribbon): one small puff, shrinking. The bullets themselves use a ribbon.
 */
export function trail(pos, color, size = 1) {
  if (!glow) return;
  const c = ldr(color, 0.55);
  smoke.emit(pos.x, pos.y, pos.z, 0, 0.3, 0, 0.22, 0.28 * size, 0.08 * size, c.r, c.g, c.b, 1, 2, 0, -1e9, 0, 0);
}

/**
 * A bullet hit. opts: { onBody: bool, count?: number, floorY?: number, dir?: {x,z}, star?: number }.
 * Five layers: the burst star, shards thrown back along the shot, puffs, sparks, a light.
 * `star` overrides the star's size (a heavy hit is ONE bigger star, never two stacked).
 */
export function impact(pos, color, opts = {}) {
  if (!glow) return;
  const onBody = !!opts.onBody;
  const fl = floorFor(pos, opts, true);
  const dir = opts.dir ?? null;
  // Shards fly BACK from the surface: opposite the shot on a wall, out and
  // around on a body.
  const bx = dir ? -dir.x : 0;
  const bz = dir ? -dir.z : 0;

  // Spikes carry less mass than petals did: a little larger to read the same.
  const starSize = opts.star ?? (onBody ? 2.2 : 1.8);
  burstStar(pos, color, starSize, starSize > 2.4 ? 0.28 : onBody ? 0.22 : 0.19);

  let c = ldr(color, 0);
  const n = count(opts.count ?? (onBody ? 6 : 5));
  for (let i = 0; i < n; i++) {
    const a = Math.random() * TAU;
    const spd = R(2.5, 5.5);
    const vx = dir ? bx * spd * 0.7 + Math.cos(a) * spd * 0.5 : Math.cos(a) * spd;
    const vz = dir ? bz * spd * 0.7 + Math.sin(a) * spd * 0.5 : Math.sin(a) * spd;
    shards.emit(pos.x, pos.y, pos.z, vx, R(1.5, 4.5), vz, R(0.32, 0.48), R(0.4, 0.58), 0.08, c.r, c.g, c.b, 1, 1.6, 15, fl, 0.3, 0);
  }
  // Deck chips on a wall: two grey chunks in the mix.
  if (!onBody) {
    _d.copy(DECK_GREY).lerp(WHITE, 0.35);
    for (let i = 0; i < 2; i++) {
      const a = Math.random() * TAU;
      shards.emit(pos.x, pos.y, pos.z, bx * 3 + Math.cos(a) * 1.5, R(2, 4), bz * 3 + Math.sin(a) * 1.5, R(0.3, 0.45), 0.3, 0.06, _d.r, _d.g, _d.b, 1, 1.6, 15, fl, 0.3, 0);
    }
  }
  c = onBody ? ldr(color, 0.6) : SMOKE;
  const puffs = count(3);
  for (let i = 0; i < puffs; i++) {
    const a = Math.random() * TAU;
    smoke.emit(
      pos.x + Math.cos(a) * 0.15, pos.y + R(-0.1, 0.2), pos.z + Math.sin(a) * 0.15,
      bx * 1.2 + Math.cos(a) * 1.4, R(0.4, 1.2), bz * 1.2 + Math.sin(a) * 1.4,
      R(0.3, 0.4), 0.45, 1.1, c.r, c.g, c.b, 1, 3, -0.3, fl, 0, 0
    );
  }
  c = hot(color, 5, 0.3);
  const sparks = count(4);
  for (let i = 0; i < sparks; i++) {
    const a = Math.random() * TAU;
    const spd = R(3, 7);
    glow.emit(pos.x, pos.y, pos.z, bx * 2 + Math.cos(a) * spd, R(1, 4), bz * 2 + Math.sin(a) * spd, R(0.12, 0.22), 0.12, 0.03, c.r, c.g, c.b, 1, 3, 12, fl, 0.35, 0.16);
  }
  c = hot(color, 1, 0.3);
  if (onBody) lights.flash(pos.x, pos.y, pos.z, c.r, c.g, c.b, 5, 4, 0.12);
  else lights.flash(pos.x, pos.y, pos.z, c.r, c.g, c.b, 3.5, 3, 0.1);
}

/** A bullet that reached max range: a small pop — a little star, three shards, a puff. */
export function expire(pos, color) {
  if (!glow) return;
  const fl = floorFor(pos, null, true);
  burstStar(pos, color, 0.7, 0.16, { erodeFrom: 0.25 });
  let c = ldr(color, 0.1);
  for (let i = 0; i < 3; i++) {
    const a = Math.random() * TAU;
    const spd = R(1.5, 3);
    shards.emit(pos.x, pos.y, pos.z, Math.cos(a) * spd, R(1, 3), Math.sin(a) * spd, R(0.22, 0.32), 0.16, 0.03, c.r, c.g, c.b, 1, 1.6, 14, fl, 0.3, 0);
  }
  c = ldr(color, 0.6);
  smoke.emit(pos.x, pos.y, pos.z, 0, 0.6, 0, 0.26, 0.3, 0.6, c.r, c.g, c.b, 1, 2, -0.3, fl, 0, 0);
}

/**
 * A detonation on the ground at `pos` (a floor point) of splash `radius`.
 * opts: { big: bool, floorColor?: hex (telegraphHex), floorY?: number }.
 * The band expands to EXACTLY `radius` — it is the splash footprint, not decoration.
 * Layers: the thick shock band, a thin fast shock line past it, the hot core
 * burst, big puffs, shards, deck chunks, a scorch, a light and a shake.
 */
export function explosion(pos, radius, color, opts = {}) {
  if (!glow) return;
  const big = !!opts.big;
  const fl = floorFor(pos, opts, false);
  const r = Math.max(0.5, radius);
  const floorColor = opts.floorColor ?? color;
  const at = { x: pos.x, y: fl, z: pos.z };

  shockBand(at, r, floorColor, big ? 0.55 : 0.42);
  let c = floorLdr(floorColor, 0.3);
  marks.ring(pos.x, fl, pos.z, r * 1.25, c.r, c.g, c.b, big ? 0.36 : 0.28, 1);
  marks.scorch(pos.x, fl, pos.z, r * 0.8);
  burstStar({ x: pos.x, y: fl + 0.6, z: pos.z }, color, Math.min(r * 1.1, 4.2), big ? 0.3 : 0.24, { pop: 0.45 });

  c = ldr(color, 0.5);
  const puffs = count(big ? 12 : 7);
  const size0 = Math.min(r * 0.45, 1.6);
  const size1 = Math.min(r * 1.0, 3.4);
  for (let i = 0; i < puffs; i++) {
    const a = (i / puffs) * TAU + R(-0.3, 0.3);
    const spd = R(0.5, 1) * r * 2.2;
    const pale = Math.random() < 0.5;
    _d.copy(pale ? SMOKE : c);
    smoke.emit(
      pos.x + Math.cos(a) * 0.3, fl + R(0.3, 0.8), pos.z + Math.sin(a) * 0.3,
      Math.cos(a) * spd, R(1, 3), Math.sin(a) * spd,
      R(0.5, 0.85), size0, size1, _d.r, _d.g, _d.b, 1, 2.6, -0.6, fl, 0, 0
    );
  }
  c = ldr(color, 0.05);
  const n = count(big ? 18 : 11);
  const spdMax = Math.min(r * 3.6, 13);
  for (let i = 0; i < n; i++) {
    const a = Math.random() * TAU;
    const spd = R(0.35, 1) * spdMax;
    shards.emit(pos.x, fl + 0.4, pos.z, Math.cos(a) * spd, R(3, 8), Math.sin(a) * spd, R(0.45, 0.8), R(0.24, 0.4), 0.05, c.r, c.g, c.b, 1, 1.4, 16, fl, 0.35, 0);
  }
  c = hot(color, 5, 0.35);
  const sparks = count(big ? 12 : 7);
  for (let i = 0; i < sparks; i++) {
    const a = Math.random() * TAU;
    const spd = R(0.4, 1) * spdMax;
    glow.emit(pos.x, fl + 0.4, pos.z, Math.cos(a) * spd, R(2, 7), Math.sin(a) * spd, R(0.25, 0.5), 0.16, 0.03, c.r, c.g, c.b, 1, 2.4, 10, fl, 0.35, 0.14);
  }
  dust(pos.x, fl, pos.z, big ? 8 : 5, r * 1.6);
  _d.set(floorColor).lerp(DECK_GREY, 0.5);
  debris.spawn(pos.x, fl + 0.3, pos.z, _d, count(big ? 10 : 5), fl, { speed: Math.min(r * 1.8, 6), up: 7, size: 0.22, life: 2.4 });
  c = hot(color, 1, 0.25);
  lights.flash(pos.x, fl + 1.1, pos.z, c.r, c.g, c.b, big ? 60 : 32, big ? 14 : 9, big ? 0.45 : 0.3);
  triggerCameraShake(big ? 0.55 : 0.24, 300, pos);
}

/** One-shot decaying light. `durationMs` in milliseconds, per the seam. */
export function flash(pos, color, intensity, distance, durationMs) {
  if (!glow) return;
  const c = hot(color, 1, 0.25);
  lights.flash(pos.x, pos.y, pos.z, c.r, c.g, c.b, intensity, distance, durationMs / 1000);
}

/** A this-frame light request; the pool picks the winners. */
export function requestLight(pos, color, intensity, distance) {
  if (!glow) return;
  const c = hot(color, 1, 0.25);
  lights.request(pos.x, pos.y, pos.z, c.r, c.g, c.b, intensity, distance);
}

/**
 * A melee swing: a filled crescent smear in the character colour with a
 * white leading edge that wipes across the sector, shards and sparks off its
 * tip, a puff at the tip, a light under it. `heavy` is the tank's swing:
 * thicker, slower, with deck dust, a shock band and a real shake.
 * opts: { heavy: bool, floorColor?: hex, floorY?: number }
 */
export function slash(pos, dir, color, range, halfAngleRad, opts = {}) {
  if (!glow) return;
  const heavy = !!opts.heavy;
  const fl = floorFor(pos, opts, true);
  const dx = dir.x;
  const dz = dir.z;
  const yaw = Math.atan2(-dx, -dz);

  const { mesh, u } = slashArc(range, (halfAngleRad * 180) / Math.PI, color);
  flatOnGround(mesh, pos.x, pos.y + 0.1, pos.z, yaw);
  mesh.rotation.x += TIP;
  mesh.renderOrder = 8;
  track(mesh, u, { life: heavy ? 0.26 : 0.18, base: range * 1.15, pop: 0.12, popT: 0.3, erodeFrom: 0.55, kind: 'slash' });

  const tipX = pos.x + dx * range * 0.8;
  const tipZ = pos.z + dz * range * 0.8;
  const px = -dz;
  const pz = dx;
  let c = ldr(color, 0.1);
  const n = count(heavy ? 6 : 4);
  for (let i = 0; i < n; i++) {
    const spd = R(3, 6);
    const side = R(-2.5, 2.5);
    shards.emit(tipX, pos.y + 0.2, tipZ, dx * spd + px * side, R(1, 4), dz * spd + pz * side, R(0.28, 0.42), heavy ? 0.3 : 0.22, 0.04, c.r, c.g, c.b, 1, 1.6, 15, fl, 0.3, 0);
  }
  c = hot(color, 5, 0.25);
  const sparks = count(heavy ? 6 : 4);
  for (let i = 0; i < sparks; i++) {
    const spd = R(4, 8);
    const side = R(-2, 2);
    glow.emit(tipX, pos.y + 0.2, tipZ, dx * spd + px * side, R(0.3, 2.5), dz * spd + pz * side, R(0.1, 0.22), 0.13, 0.03, c.r, c.g, c.b, 1, 3.5, 8, fl, 0.35, 0.16);
  }
  c = ldr(color, 0.6);
  smoke.emit(tipX, pos.y, tipZ, dx * 1.5, 0.5, dz * 1.5, 0.28, 0.35, 0.8, c.r, c.g, c.b, 1, 3, -0.3, fl, 0, 0);
  c = hot(color, 1, 0.3);
  lights.flash(pos.x + dx * range * 0.5, pos.y, pos.z + dz * range * 0.5, c.r, c.g, c.b, heavy ? 8 : 4, heavy ? 5 : 3.5, 0.09);

  if (heavy) {
    const cx = pos.x + dx * range * 0.45;
    const cz = pos.z + dz * range * 0.45;
    dust(cx, fl, cz, 6, range * 0.9);
    shockBand({ x: cx, y: fl, z: cz }, range * 0.6, opts.floorColor ?? color, 0.3, 0.45);
    _d.set(opts.floorColor ?? color).lerp(DECK_GREY, 0.5);
    debris.spawn(cx, fl + 0.2, cz, _d, count(3), fl, { speed: 3, up: 5, size: 0.22, life: 2 });
    triggerCameraShake(0.35, 220, pos);
  } else {
    triggerCameraShake(0.12, 120, pos);
  }
}

// ─── Airborne things: ribbons and shadows ───────────────────────

/** A continuous tapered trail. Returns a handle for ribbonPush/ribbonRelease, or -1. */
export function ribbon(color, width = 0.35, length = 3, whiten = 0) {
  if (!glow) return -1;
  const c = ldr(color, whiten);
  return ribbons.acquire(c.r, c.g, c.b, width, length);
}

export function ribbonPush(id, pos) {
  if (!glow || id < 0) return;
  ribbons.push(id, pos.x, pos.y, pos.z);
}

export function ribbonRelease(id) {
  if (!glow || id < 0) return;
  ribbons.release(id);
}

/** A blob shadow under something airborne, this frame only. */
export function shadow(pos, floorY, radius = 0.4, strength = 0.4) {
  if (!glow) return;
  shadows.add(pos.x, floorY, pos.z, radius, strength);
}

// ─── The rest of the kit ────────────────────────────────────────

/** A ring of tan dust puffs on the deck, low and spreading. */
export function dust(x, y, z, n, spread) {
  if (!glow) return;
  const c = count(n);
  for (let i = 0; i < c; i++) {
    const a = (i / c) * TAU + R(-0.4, 0.4);
    const spd = R(0.5, 1) * spread;
    smoke.emit(
      x + Math.cos(a) * 0.25, y + 0.12, z + Math.sin(a) * 0.25,
      Math.cos(a) * spd, R(0.3, 0.9), Math.sin(a) * spd,
      R(0.4, 0.6), 0.32, 0.85, DUST.r, DUST.g, DUST.b, 1, 3, -0.15, y, 0, 0
    );
  }
}

/**
 * A ground slam of `radius` — the tank's signature, and it has to hit HARD.
 * Layers on their own clocks: a thick cel-banded shock band at the TRUE radius
 * (the footprint, held then eroded), a thin shock line racing past it, ink
 * CRACKS across the deck that close from the rim in, a short strong core pop,
 * a heavy ring of dust, deck chunks and shards thrown outward, a scorch, a
 * light and a shove of shake. opts: { floorColor?, floorY? }.
 */
export function slam(pos, radius, color, opts = {}) {
  if (!glow) return;
  const fl = floorFor(pos, opts, false);
  const r = Math.max(0.8, radius);
  const floorColor = opts.floorColor ?? color;
  const at = { x: pos.x, y: fl, z: pos.z };
  shockBand(at, r, floorColor, 0.6, Math.min(Math.max(0.7, r * 0.4), 2.6));
  let c = floorLdr(floorColor, 0.3);
  marks.ring(pos.x, fl, pos.z, r * 1.3, c.r, c.g, c.b, 0.36, 1);
  marks.scorch(pos.x, fl, pos.z, r * 0.7);
  // The cracks: identity colour (ink is near-black either way), at the radius.
  const { mesh: crack, u: cu } = cracksShape(color);
  flatOnGround(crack, pos.x, fl + 0.01, pos.z);
  crack.rotation.z = Math.random() * TAU;
  crack.renderOrder = 5;
  track(crack, cu, { life: 0.9, base: r * 0.95, pop: 0, popT: 0.2, erodeFrom: 0.35, kind: 'fixed' });
  // A short, strong pop: gone in 160 ms so the band and the cracks own the frame.
  burstStar({ x: pos.x, y: fl + 0.6, z: pos.z }, color, Math.min(r * 0.38, 2.0), 0.16, { pop: 0.5, erodeFrom: 0.22 });
  // The dust wall: a heavy ring, sized to the slam.
  const puffs = count(18);
  const ps = Math.min(0.45 + r * 0.06, 0.9);
  for (let i = 0; i < puffs; i++) {
    const a = (i / puffs) * TAU + R(-0.2, 0.2);
    const spd = R(0.7, 1) * r * 2.4;
    smoke.emit(pos.x + Math.cos(a) * 0.4, fl + R(0.15, 0.5), pos.z + Math.sin(a) * 0.4, Math.cos(a) * spd, R(0.6, 1.8), Math.sin(a) * spd, R(0.5, 0.8), ps, ps * 2.3, DUST.r, DUST.g, DUST.b, 1, 3.2, -0.2, fl, 0, 0);
  }
  c = ldr(color, 0.05);
  const n = count(18);
  for (let i = 0; i < n; i++) {
    const a = Math.random() * TAU;
    const spd = R(2.5, 8) * Math.min(1.6, r / 4);
    shards.emit(pos.x, fl + 0.2, pos.z, Math.cos(a) * spd, R(3, 9), Math.sin(a) * spd, R(0.5, 0.9), R(0.3, 0.45), 0.06, c.r, c.g, c.b, 1, 1.4, 16, fl, 0.35, 0);
  }
  c = hot(color, 5, 0.2);
  const sparks = count(8);
  for (let i = 0; i < sparks; i++) {
    const a = Math.random() * TAU;
    const spd = R(2, 7) * Math.min(1.6, r / 4);
    glow.emit(pos.x, fl + 0.2, pos.z, Math.cos(a) * spd, R(1, 4), Math.sin(a) * spd, R(0.25, 0.5), 0.16, 0.04, c.r, c.g, c.b, 1, 2.5, 8, fl, 0.35, 0.14);
  }
  _d.set(floorColor).lerp(DECK_GREY, 0.6);
  debris.spawn(pos.x, fl + 0.3, pos.z, _d, count(14), fl, { speed: Math.min(r * 1.5, 7), up: 8, size: 0.28, life: 2.4 });
  c = hot(color, 1, 0.25);
  lights.flash(pos.x, fl + 0.9, pos.z, c.r, c.g, c.b, 40, 10, 0.32);
  triggerCameraShake(0.55, 300, pos);
}

/**
 * A body defeated at `pos` (chest point): the big cartoon POOF — a cloud of
 * pale puffs in the fallen colour popping outward, confetti shards thrown up
 * and tumbling down, a burst star, a floor band, a light and a shake.
 * opts: { floorColor?, floorY? }.
 */
export function defeat(pos, color, opts = {}) {
  if (!glow) return;
  const fl = floorFor(pos, opts, true);
  // The star is the frame-one flash and gets out of the way: the CLOUD is the
  // death. It starts big and jumps outward at once (high drag), so it is a
  // two-unit poof by the third frame instead of hiding behind the star.
  burstStar(pos, color, 2.0, 0.2, { pop: 0.45, erodeFrom: 0.2 });
  shockBand({ x: pos.x, y: fl, z: pos.z }, 1.7, opts.floorColor ?? color, 0.4, 0.5);

  let c = ldr(color, 0.4);
  const puffs = count(10);
  for (let i = 0; i < puffs; i++) {
    const a = (i / puffs) * TAU + R(-0.3, 0.3);
    const spd = R(4, 7);
    const pale = i % 3 === 0;
    _d.copy(pale ? SMOKE : c);
    smoke.emit(
      pos.x + Math.cos(a) * 0.5, pos.y + R(-0.6, 0.6), pos.z + Math.sin(a) * 0.5,
      Math.cos(a) * spd, R(0.5, 2.5), Math.sin(a) * spd,
      R(0.6, 0.95), R(1.0, 1.3), R(1.7, 2.2), _d.r, _d.g, _d.b, 1, 6, -0.4, fl, 0, 0
    );
  }
  // Confetti: the fallen colour and white, thrown high, tumbling down.
  const n = count(16);
  for (let i = 0; i < n; i++) {
    const a = Math.random() * TAU;
    const spd = R(1.5, 4.5);
    const white = i % 3 === 0;
    _d.copy(white ? WHITE : ldr(color, 0.05));
    shards.emit(pos.x, pos.y + 0.3, pos.z, Math.cos(a) * spd, R(4, 8), Math.sin(a) * spd, R(0.9, 1.3), R(0.2, 0.3), 0.06, _d.r, _d.g, _d.b, 1, 1.2, 11, fl, 0.3, 0);
  }
  c = hot(color, 5, 0.3);
  const sparks = count(10);
  for (let i = 0; i < sparks; i++) {
    const a = Math.random() * TAU;
    const spd = R(2, 5);
    glow.emit(pos.x, pos.y, pos.z, Math.cos(a) * spd, R(1, 4.5), Math.sin(a) * spd, R(0.3, 0.6), 0.16, 0.03, c.r, c.g, c.b, 1, 2.2, 8, fl, 0.35, 0.12);
  }
  debris.spawn(pos.x, fl + 0.5, pos.z, color, count(4), fl, { speed: 3.5, up: 6, size: 0.22, life: 2.2 });
  c = hot(color, 1, 0.25);
  lights.flash(pos.x, fl + 1, pos.z, c.r, c.g, c.b, 26, 8, 0.4);
  triggerCameraShake(0.3, 260, pos);
}

/** A radial burst from a chest point: shards and sparks. opts: { count, speed, floorY }. */
export function burst(pos, color, opts = {}) {
  if (!glow) return;
  const fl = floorFor(pos, opts, true);
  const speed = opts.speed ?? 5;
  const n = count(opts.count ?? 12);
  let c = ldr(color, 0.05);
  for (let i = 0; i < n; i++) {
    const a = Math.random() * TAU;
    const spd = R(0.4, 1) * speed;
    shards.emit(pos.x, pos.y, pos.z, Math.cos(a) * spd, R(1, 4.5), Math.sin(a) * spd, R(0.35, 0.6), 0.22, 0.04, c.r, c.g, c.b, 1, 1.6, 12, fl, 0.3, 0);
  }
  c = hot(color, 5, 0.25);
  const sparks = count(Math.max(3, (opts.count ?? 12) >> 1));
  for (let i = 0; i < sparks; i++) {
    const a = Math.random() * TAU;
    const spd = R(0.4, 1) * speed;
    glow.emit(pos.x, pos.y, pos.z, Math.cos(a) * spd, R(1, 4.5), Math.sin(a) * spd, R(0.3, 0.5), 0.14, 0.03, c.r, c.g, c.b, 1, 2.2, 8, fl, 0.35, 0.12);
  }
}

/**
 * One stride's dust at the feet. Off where the tier says so (`footsteps: false`,
 * the low tier) — an explicit flag, because a threshold on fxScale silently
 * stopped matching the day the low tier's scale moved.
 */
export function footstep(pos) {
  if (!glow || !_footsteps) return;
  smoke.emit(pos.x + R(-0.1, 0.1), pos.y + 0.06, pos.z + R(-0.1, 0.1), R(-0.3, 0.3), 0.4, R(-0.3, 0.3), 0.34, 0.16, 0.42, DUST.r, DUST.g, DUST.b, 1, 2.5, -0.1, pos.y, 0, 0);
}

/**
 * Landing dust at the feet: a low ring of puffs kicked outward and a quick
 * shock line, scaled by `strength` (0..1, the fall's weight).
 */
export function landing(pos, strength = 0.5) {
  if (!glow) return;
  const s = 0.5 + strength;
  const n = count(6 + Math.round(strength * 4));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + R(-0.3, 0.3);
    const spd = R(1.6, 3) * s;
    smoke.emit(pos.x + Math.cos(a) * 0.3, pos.y + 0.1, pos.z + Math.sin(a) * 0.3, Math.cos(a) * spd, R(0.3, 0.9), Math.sin(a) * spd, R(0.34, 0.5), 0.3 * s, 0.8 * s, DUST.r, DUST.g, DUST.b, 1, 3.2, -0.1, pos.y, 0, 0);
  }
  marks.ring(pos.x, pos.y, pos.z, 1.2 * s, DUST.r, DUST.g, DUST.b, 0.24, 1);
}

/**
 * A dash: speed smears peeling off the body opposite its travel, a spray of
 * dust at the feet, and a light. `dir` is the travel direction on the ground.
 * opts: { floorY? }
 */
export function dash(pos, dir, color, opts = {}) {
  if (!glow) return;
  const fl = floorFor(pos, opts, true);
  const dx = dir.x;
  const dz = dir.z;
  const yaw = Math.atan2(-dx, -dz);
  const px = -dz;
  const pz = dx;
  const n = count(3);
  for (let i = 0; i < n; i++) {
    const { mesh, u } = smear(color);
    const side = (i - (n - 1) / 2) * 0.42;
    flatOnGround(mesh, pos.x - dx * 0.9 + px * side, pos.y + 0.15 + i * 0.12, pos.z - dz * 0.9 + pz * side, yaw);
    mesh.rotation.x += TIP;
    mesh.scale.set(0.6, 2.6 + i * 0.4, 1);
    mesh.renderOrder = 8;
    _scene?.add(mesh);
    _tracked.push({ mesh, u, age: 0, life: 0.2 + i * 0.03, base: 1, pop: 0, popT: 0.2, erodeFrom: 0.3, kind: 'fixed' });
  }
  const puffs = count(5);
  for (let i = 0; i < puffs; i++) {
    const side = R(-0.5, 0.5);
    smoke.emit(pos.x - dx * 0.3 + px * side, fl + 0.1, pos.z - dz * 0.3 + pz * side, -dx * R(1.5, 3) + px * side * 2, R(0.4, 1), -dz * R(1.5, 3) + pz * side * 2, R(0.3, 0.45), 0.28, 0.7, DUST.r, DUST.g, DUST.b, 1, 3, -0.15, fl, 0, 0);
  }
  const c = hot(color, 1, 0.3);
  lights.flash(pos.x, pos.y, pos.z, c.r, c.g, c.b, 8, 4, 0.16);
}

/**
 * A few tiny sparks drifting up from around `pos` — the per-frame trickle a
 * status aura leaves (ice specks off a slowed body, red ticks off one being
 * drained). No light, no shape: the cheapest thing in the kit.
 * opts: { up?: units/s, spread?: u, size?: u, life?: s, gain?: hdr gain }
 */
export function specks(pos, color, n = 1, opts = {}) {
  if (!glow) return;
  const c = hot(color, opts.gain ?? 3, 0.25);
  const k = count(n);
  const spread = opts.spread ?? 0.45;
  const up = opts.up ?? 1.2;
  for (let i = 0; i < k; i++) {
    glow.emit(
      pos.x + R(-spread, spread), pos.y + R(-0.5, 0.5), pos.z + R(-spread, spread),
      R(-0.2, 0.2), up * R(0.7, 1.3), R(-0.2, 0.2),
      opts.life ?? 0.45, opts.size ?? 0.16, 0.04, c.r, c.g, c.b, 0.9, 0.6, 0, -1e9, 0, 0
    );
  }
}

/**
 * A few cartoon puffs from `pos` with a given vertical push: thruster
 * exhaust falling away under a hovering body (`up` < 0), or smoke rising.
 * opts: { up?: units/s, spread?: u, size?: u, life?: s, floorY?: number, pale?: boolean }
 */
export function puffs(pos, color, n = 2, opts = {}) {
  if (!glow) return;
  const c = opts.pale ? SMOKE : ldr(color, 0.55);
  const k = count(n);
  const spread = opts.spread ?? 0.3;
  const size = opts.size ?? 0.32;
  const fl = Number.isFinite(opts.floorY) ? opts.floorY : -1e9;
  for (let i = 0; i < k; i++) {
    const a = Math.random() * TAU;
    smoke.emit(
      pos.x + Math.cos(a) * spread * 0.3, pos.y, pos.z + Math.sin(a) * spread * 0.3,
      Math.cos(a) * spread * 2, opts.up ?? 0.8, Math.sin(a) * spread * 2,
      opts.life ?? 0.4, size, size * 2.2, c.r, c.g, c.b, 1, 2.6, opts.gravity ?? -0.2, fl, 0, 0
    );
  }
}

/** Sparks that rise off a body: buffs, heals. Round and soft on purpose. */
export function rise(pos, color, n = 6) {
  if (!glow) return;
  let c = hot(color, 3.2, 0.3);
  const k = count(n);
  for (let i = 0; i < k; i++) {
    glow.emit(
      pos.x + R(-0.45, 0.45), pos.y + R(-0.6, 0.4), pos.z + R(-0.45, 0.45),
      0, R(0.8, 1.6), 0,
      R(0.4, 0.7), 0.22, 0.05, c.r, c.g, c.b, 0.9, 0.5, 0, -1e9, 0, 0
    );
  }
  c = hot(color, 1, 0.3);
  lights.flash(pos.x, pos.y, pos.z, c.r, c.g, c.b, 10, 4, 0.3);
}

/** Energy converging on a point from `radius` out, arriving in `arriveS`. Shards, point-first. */
export function implode(pos, radius, color, n = 16, arriveS = 0.45) {
  if (!glow) return;
  const c = ldr(color, 0.15);
  const k = count(n);
  const T = Math.max(0.12, arriveS);
  const v = radius / T;
  for (let i = 0; i < k; i++) {
    const a = Math.random() * TAU;
    const rr = radius * R(0.85, 1);
    shards.emit(
      pos.x + Math.cos(a) * rr, pos.y + R(-0.2, 0.6), pos.z + Math.sin(a) * rr,
      -Math.cos(a) * v, 0, -Math.sin(a) * v,
      T * 1.05, 0.26, 0.1, c.r, c.g, c.b, 1, 0, 0, -1e9, 0, 0.12
    );
  }
}

/**
 * Deck dust DRAGGED in along the floor toward (x, y, z) from `radius` out,
 * arriving in `arriveS`: the gravity well's "everything slides in".
 */
export function drag(x, y, z, radius, n = 10, arriveS = 1) {
  if (!glow) return;
  const k = count(n);
  const T = Math.max(0.2, arriveS);
  for (let i = 0; i < k; i++) {
    const a = (i / k) * TAU + R(-0.3, 0.3);
    const rr = radius * R(0.6, 1);
    const v = rr / T;
    smoke.emit(
      x + Math.cos(a) * rr, y + 0.12, z + Math.sin(a) * rr,
      -Math.cos(a) * v, 0.25, -Math.sin(a) * v,
      T * 0.95, 0.34, 0.6, DUST.r, DUST.g, DUST.b, 1, 0.2, 0, y, 0, 0.1
    );
  }
}

/** A burst of ice: chunky pale-blue shards and a few specks, from a floor point. The trap's SLOWED read. */
export function frost(pos, color, n = 8) {
  if (!glow) return;
  _c.set(color);
  const k = count(n);
  for (let i = 0; i < k; i++) {
    const a = Math.random() * TAU;
    const spd = R(1.5, 4);
    shards.emit(pos.x, pos.y + 0.2, pos.z, Math.cos(a) * spd, R(2, 5), Math.sin(a) * spd, R(0.4, 0.7), R(0.28, 0.4), 0.06, _c.r, _c.g, _c.b, 1, 1.6, 14, pos.y, 0.3, 0);
  }
  specks({ x: pos.x, y: pos.y + 0.6, z: pos.z }, color, Math.max(2, k >> 1), { up: 1.4, spread: 0.6, size: 0.15, life: 0.6 });
}

/** A camera shake from a world point, through the kit (so callers need no ScreenEffects import). */
export function shake(intensity, at = null) {
  triggerCameraShake(intensity, 200, at);
}

/** Sparks strewn along a segment, flying along it: dashes, fault lines. */
export function streak(from, to, color, n = 12) {
  if (!glow) return;
  const c = hot(color, 4, 0.2);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const len = Math.hypot(dx, dz) || 1;
  const ux = dx / len;
  const uz = dz / len;
  const fl = Math.min(from.y, to.y) - PLAYER_HALF_HEIGHT;
  const k = count(n);
  for (let i = 0; i < k; i++) {
    const s = Math.random();
    const spd = R(2, 6);
    glow.emit(
      from.x + dx * s + R(-0.3, 0.3), from.y + dy * s + R(-0.3, 0.3), from.z + dz * s + R(-0.3, 0.3),
      ux * spd, R(-0.3, 0.8), uz * spd,
      R(0.15, 0.3), 0.16, 0.04, c.r, c.g, c.b, 1, 2, 2, fl, 0.35, 0.16
    );
  }
}

/** A floor band on its own (a heavy hit's footprint). Colour is a FLOOR colour. */
export function shockRing(pos, radius, floorColor, durationMs = 360) {
  if (!glow) return;
  shockBand(pos, radius, floorColor, durationMs / 1000);
}

/** Deck chunks on their own. `color` is a FLOOR colour, blended toward deck grey. */
export function chunks(pos, floorColor, n = 8, floorY = pos.y) {
  if (!glow) return;
  _d.set(floorColor).lerp(DECK_GREY, 0.5);
  debris.spawn(pos.x, pos.y + 0.3, pos.z, _d, count(n), floorY);
}
